import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";
import useModuleAccess from "../hooks/useModuleAccess";

const API_URL = process.env.REACT_APP_API_URL;

const RfqStatCardSkeleton = () => (
  <div className="procurement-stat-col">
    <div className="card supplier-stat-card">
      <div className="card-body">
        <div className="stat-skeleton stat-skeleton-title" aria-hidden="true" />
        <div className="stat-skeleton stat-skeleton-value" aria-hidden="true" />
      </div>
    </div>
  </div>
);

const RFQ_STAT_CARDS = [
  {
    key: "totalRfqs",
    label: "Total RFQ",
    titleColor: "#1F4E79",
    valueColor: "#2874A6",
    filterType: null,
    filterValue: null,
  },
  {
    key: "openRfqs",
    label: "Open-Quotes Not Received",
    titleColor: "#196F3D",
    valueColor: "#27AE60",
    filterType: "status",
    filterValue: "open",
  },
  {
    key: "quotesReceivedRfqs",
    label: "Open-Quotes Received",
    titleColor: "#6C3483",
    valueColor: "#8E44AD",
    filterType: "status",
    filterValue: "Quotes Received",
  },
  {
    key: "poCreatedRfqs",
    label: "PO Created",
    titleColor: "#7D6608",
    valueColor: "#B7950B",
    filterType: "status",
    filterValue: "Po created",
  },
  {
    key: "poPartiallyCreatedRfqs",
    label: "PO Partially Created",
    titleColor: "#B45309",
    valueColor: "#D97706",
    filterType: "status",
    filterValue: "Po Partially created",
  },
  {
    key: "closedRfqs",
    label: "Closed-Quotes Not Received",
    titleColor: "#424949",
    valueColor: "#7F8C8D",
    filterType: "status",
    filterValue: "closed",
  },
  {
    key: "closedQuoteReceivedRfqs",
    label: "Closed - Quotes Received",
    titleColor: "#5D6D7E",
    valueColor: "#85929E",
    filterType: "status",
    filterValue: "closed-quote received",
  },
  {
    key: "draftRfqs",
    label: "Draft",
    titleColor: "#424949",
    valueColor: "#7F8C8D",
    filterType: "recordStatus",
    filterValue: "Draft",
  },
];

const RfqStatCard = ({
  label,
  value,
  titleColor,
  valueColor,
  onClick,
  isActive,
}) => (
  <div className="procurement-stat-col">
    <div
      className={`card supplier-stat-card procurement-stat-card-clickable${
        isActive ? " procurement-stat-card-active" : ""
      }`}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      role="button"
      tabIndex={0}
      title={`Filter by ${label}`}
    >
      <div className="card-body">
        <div className="stat-title" style={{ color: titleColor }}>
          {label}
        </div>
        <div className="stat-value" style={{ color: valueColor }}>
          {value}
        </div>
      </div>
    </div>
  </div>
);

function parseApiResponse(raw) {
  let d = raw;
  if (typeof d === "string") {
    try {
      d = JSON.parse(d);
    } catch {
      return raw;
    }
  }
  if (d?.body != null) {
    if (typeof d.body === "string") {
      try {
        return JSON.parse(d.body);
      } catch {
        return d.body;
      }
    }
    return d.body;
  }
  return d;
}

function parseJsonIfString(value) {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function normalizeApiPayload(raw) {
  let obj = raw;
  if (!obj || typeof obj !== "object") return raw;
  obj = { ...obj };
  if (typeof obj.data === "string") {
    obj.data = parseJsonIfString(obj.data);
  }
  if (
    obj.data &&
    typeof obj.data === "object" &&
    typeof obj.data.data === "string"
  ) {
    obj.data = { ...obj.data, ...parseJsonIfString(obj.data.data) };
  }
  return obj;
}

function isApiSuccess(responseData) {
  if (!responseData || typeof responseData !== "object") return false;
  const s = responseData.success;
  return s === true || s === "true" || s === 1 || s === "1";
}

function extractRfqsPayload(responseData) {
  const root = responseData;
  let dataBlock = root?.data != null ? root.data : root;

  if (typeof dataBlock === "string") {
    dataBlock = parseJsonIfString(dataBlock);
  }
  if (!dataBlock || typeof dataBlock !== "object") {
    return { rfqs: [], pagination: {} };
  }

  if (Array.isArray(dataBlock)) {
    return {
      rfqs: dataBlock,
      pagination:
        root.pagination && typeof root.pagination === "object"
          ? root.pagination
          : {},
    };
  }

  let list =
    dataBlock.rfqs || dataBlock.rfqList || dataBlock.items || root.rfqs;

  if (list == null && dataBlock.data != null) {
    const inner =
      typeof dataBlock.data === "string"
        ? parseJsonIfString(dataBlock.data)
        : dataBlock.data;
    if (inner && typeof inner === "object") {
      list = inner.rfqs || inner.rfqList || inner.items;
    }
  }

  if (
    (list == null || (Array.isArray(list) && list.length === 0)) &&
    dataBlock.rfqs == null &&
    dataBlock.rfqList == null &&
    dataBlock.items == null &&
    (dataBlock._id != null ||
      dataBlock.rfqNo != null ||
      dataBlock.rfqNumber != null)
  ) {
    list = [dataBlock];
  }

  const pagination =
    (dataBlock && dataBlock.pagination) || root.pagination || {};

  if (typeof list === "string") {
    list = parseJsonIfString(list);
  }

  const total =
    typeof pagination.total === "number"
      ? pagination.total
      : typeof pagination.totalCount === "number"
        ? pagination.totalCount
        : typeof dataBlock.totalCount === "number"
          ? dataBlock.totalCount
          : typeof dataBlock.total === "number"
            ? dataBlock.total
            : undefined;

  return {
    rfqs: Array.isArray(list) ? list : [],
    pagination:
      pagination && typeof pagination === "object" && !Array.isArray(pagination)
        ? { ...pagination, ...(total != null ? { total } : {}) }
        : total != null
          ? { total }
          : {},
  };
}

function formatRfqDate(value) {
  if (!value) return "-";
  const str = String(value).trim();
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) return str;
  const dateTimeMatch = str.match(/^(\d{2}-\d{2}-\d{4})[ T](\d{2}:\d{2})/);
  if (dateTimeMatch) {
    return `${dateTimeMatch[1]} ${dateTimeMatch[2]}`;
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return str || "-";
  return d.toLocaleDateString();
}

function rfqNumber(rfq) {
  return rfq?.rfqNo || rfq?.rfqNumber || rfq?.rfqId || rfq?.referenceNo || "-";
}

function rfqDateValue(rfq) {
  return formatRfqDate(rfq?.rfqDate);
}

function rfqDueDateValue(rfq) {
  return formatRfqDate(rfq?.rfqDueDate || rfq?.dueDate);
}

function getRfqPrNumbers(rfq) {
  if (Array.isArray(rfq?.prNumbers) && rfq.prNumbers.length > 0) {
    const numbers = new Set();
    rfq.prNumbers.forEach((prNumber) => {
      const value = String(prNumber || "").trim();
      if (value) numbers.add(value);
    });
    return [...numbers];
  }

  const materials = Array.isArray(rfq?.materials) ? rfq.materials : [];
  const numbers = new Set();
  materials.forEach((material) => {
    const prNumber = String(material?.prNumber || material?.prNo || "").trim();
    if (prNumber) numbers.add(prNumber);
  });
  return [...numbers];
}

function renderRfqPrNumbers(rfq) {
  const prNumbers = getRfqPrNumbers(rfq);
  if (prNumbers.length === 0) return "-";
  return (
    <span style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
      {prNumbers.join(", ")}
    </span>
  );
}

function getRfqClientReferences(rfq) {
  if (Array.isArray(rfq?.clientReferences) && rfq.clientReferences.length > 0) {
    const references = new Set();
    rfq.clientReferences.forEach((clientReference) => {
      const value = String(clientReference || "").trim();
      if (value) references.add(value);
    });
    return [...references];
  }

  const materials = Array.isArray(rfq?.materials) ? rfq.materials : [];
  const references = new Set();
  materials.forEach((material) => {
    const clientReference = String(material?.clientReference || "").trim();
    if (clientReference) references.add(clientReference);
  });
  return [...references];
}

function renderRfqClientReferences(rfq) {
  const clientReferences = getRfqClientReferences(rfq);
  if (clientReferences.length === 0) return "-";
  return (
    <span style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
      {clientReferences.join(", ")}
    </span>
  );
}

function rfqSummaryValue(rfq) {
  const s = rfq?.rfqSummary || rfq?.summary || "";
  return s.trim() ? s : "-";
}

function rfqCreatedByName(rfq) {
  const createdBy = rfq?.createdBy;
  if (!createdBy) return "-";
  if (typeof createdBy === "string") return createdBy.trim() || "-";
  return createdBy.name || createdBy.userName || "-";
}

function getRfqSuppliers(rfq) {
  if (Array.isArray(rfq?.suppliers) && rfq.suppliers.length > 0) {
    return rfq.suppliers.filter(
      (s) =>
        String(s.supplierMailId || "").trim() ||
        String(s.supplierName || "").trim(),
    );
  }
  const legacyMail =
    rfq?.supplierMailId || rfq?.supplierEmail || rfq?.supplierMail || "";
  if (String(legacyMail).trim()) {
    return [
      {
        supplierName: rfq?.supplierName || "-",
        supplierMailId: String(legacyMail).trim(),
      },
    ];
  }
  return [];
}

function capitalizeStatus(value) {
  if (!value) return "-";
  const s = String(value);
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

function supplierHasSubmittedQuote(supplier) {
  if (!supplier || typeof supplier !== "object") return false;
  if (supplier.hasQuoteSubmitted === true) return true;
  if (supplier.quoteSubmittedAt) return true;
  if (
    Array.isArray(supplier.materialQuotes) &&
    supplier.materialQuotes.length > 0
  )
    return true;
  if (Array.isArray(supplier.terms) && supplier.terms.length > 0) return true;
  return false;
}

function countSuppliersWithQuotes(suppliers) {
  return suppliers.filter(supplierHasSubmittedQuote).length;
}

const quoteCountBadgeStyle = {
  padding: "1px 8px",
  fontSize: "11px",
  lineHeight: 1.4,
};

function formatRfqStatusWithQuoteCount(rfq) {
  const baseStatus = capitalizeStatus(rfq?.rfqStatus);
  const quotesSummary = rfq?.quotesSummary;

  if (quotesSummary && typeof quotesSummary === "object") {
    const submitted =
      typeof quotesSummary.submitted === "number" ? quotesSummary.submitted : 0;
    const total =
      typeof quotesSummary.total === "number" ? quotesSummary.total : 0;

    if (total > 0) {
      return (
        <span
          className="d-inline-flex align-items-center flex-wrap"
          style={{ gap: "6px" }}
        >
          <span>{baseStatus}</span>
          <span className="btn btn-sm btn-blue" style={quoteCountBadgeStyle}>
            {submitted}/{total}
          </span>
        </span>
      );
    }
  }

  const suppliers = getRfqSuppliers(rfq);
  const totalSuppliers = suppliers.length;

  if (totalSuppliers === 0) return baseStatus;

  const quotedCount =
    typeof rfq?.quotesReceivedCount === "number"
      ? rfq.quotesReceivedCount
      : typeof rfq?.suppliersWithQuotes === "number"
        ? rfq.suppliersWithQuotes
        : countSuppliersWithQuotes(suppliers);

  return (
    <span
      className="d-inline-flex align-items-center flex-wrap"
      style={{ gap: "6px" }}
    >
      <span>{baseStatus}</span>
      <span className="btn btn-sm btn-blue" style={quoteCountBadgeStyle}>
        {quotedCount}/{totalSuppliers}
      </span>
    </span>
  );
}

function statusBadgeClass(status) {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "active") return "badge-success";
  if (normalized === "draft") return "badge-secondary";
  return "badge-info";
}

function renderRfqRecordStatus(status) {
  const normalized = String(status || "").toLowerCase();
  const label = capitalizeStatus(status);

  if (normalized === "inactive") {
    return (
      <span
        className="fwt_600 f_red"
        style={{ fontSize: "90%", fontWeight: 550 }}
      >
        {label}
      </span>
    );
  }

  if (normalized === "active") {
    return (
      <span
        className="fwt_600 f_green"
        style={{ fontSize: "90%", fontWeight: 550 }}
      >
        {label}
      </span>
    );
  }

  return (
    <span
      className={`badge ${statusBadgeClass(status)}`}
      style={{ fontSize: "90%", fontWeight: 550 }}
    >
      {label}
    </span>
  );
}

function normalizeRfqUsers(users) {
  if (!Array.isArray(users)) return [];
  return users
    .map((user) => ({
      _id: String(user._id || user.id || "").trim(),
      name: String(user.name || user.userName || user.email || "").trim(),
    }))
    .filter((user) => user._id && user.name);
}

function normalizeRfqClientReferences(clients) {
  if (!Array.isArray(clients)) return [];
  return clients
    .map((client) => ({
      clientReferenceId: String(
        client.clientReferenceId || client._id || client.id || "",
      ).trim(),
      clientReference: String(
        client.clientReference || client.clientName || client.name || "",
      ).trim(),
    }))
    .filter((client) => client.clientReferenceId && client.clientReference);
}

const RFQ_RECORD_STATUS_OPTIONS = ["Active", "Draft", "Inactive"];
const RFQ_STATUS_OPTIONS = [
  { value: "open", label: "Open" },
  { value: "closed", label: "Closed" },
  { value: "closed-quote received", label: "Closed - QuotesReceived" },
  { value: "Quotes Received", label: "Quotes Received" },
  { value: "Po created", label: "PO Created" },
  { value: "Po Partially created", label: "PO Partially Created" },
];

const RFQ_SORT_COLUMN_MAP = {
  rfqNumber: "rfqNumber",
  rfqDate: "rfqDate",
  rfqDueDate: "rfqDueDate",
  status: "status",
  rfqStatus: "rfqStatus",
  createdBy: "createdBy",
};

const DEFAULT_SORT_BY = "createdAt";
const DEFAULT_SORT_ORDER = "desc";

const DEFAULT_FILTER_CHECKS = {
  status: false,
  recordStatus: false,
  clientReference: false,
  createdBy: false,
  dateRange: false,
};
const DEFAULT_FILTER_INPUTS = {
  status: "",
  recordStatus: "",
  clientReference: "",
  createdBy: "",
  fromDate: "",
  toDate: "",
};
const DEFAULT_ACTIVE_MULTI_FILTERS = {
  status: "",
  recordStatus: "",
  clientReference: "",
  createdBy: "",
  fromDate: "",
  toDate: "",
};
const MAX_ACTIVE_FILTERS = 2; 

const Rfq = () => {
  const {
    canCreate: canCreateRfq,
    canView: canViewRfq,
    canUpdate: canUpdateRfq,
    canDelete: canDeleteRfq,
  } = useModuleAccess("rfq");
  const navigate = useNavigate();

  const savedFilters = JSON.parse(sessionStorage.getItem("rfqFilters") || "{}");

  const [rfqs, setRfqs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(
    savedFilters.searchQuery || "",
  );
  const [activeSearchQuery, setActiveSearchQuery] = useState(
    savedFilters.activeSearchQuery || "",
  );
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(savedFilters.currentPage || 1);
  const [itemsPerPage, setItemsPerPage] = useState(
    savedFilters.itemsPerPage || 25,
  );
  const [deleteRfqId, setDeleteRfqId] = useState(null);
  const [deleteRfqNumber, setDeleteRfqNumber] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [filterChecks, setFilterChecks] = useState(
    savedFilters.filterChecks || DEFAULT_FILTER_CHECKS,
  );
  const [filterInputs, setFilterInputs] = useState(
    savedFilters.filterInputs || DEFAULT_FILTER_INPUTS,
  );
  const [activeMultiFilters, setActiveMultiFilters] = useState(
    savedFilters.activeMultiFilters || DEFAULT_ACTIVE_MULTI_FILTERS,
  );
  const filterPanelRef = useRef(null);

  const [rfqUsers, setRfqUsers] = useState([]);
  const [isRfqUsersLoading, setIsRfqUsersLoading] = useState(false);
  const [rfqClientReferences, setRfqClientReferences] = useState([]);
  const [isRfqClientReferencesLoading, setIsRfqClientReferencesLoading] =
    useState(false);
  const [sortBy, setSortBy] = useState(savedFilters.sortBy || DEFAULT_SORT_BY);
  const [sortOrder, setSortOrder] = useState(
    savedFilters.sortOrder || DEFAULT_SORT_ORDER,
  );
  const [isRfqCountLoading, setIsRfqCountLoading] = useState(true);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportMode, setExportMode] = useState("all");
  const [exportFromDate, setExportFromDate] = useState("");
  const [exportToDate, setExportToDate] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const [rfqCount, setRfqCount] = useState({
    totalRfqs: 0,
    openRfqs: 0,
    closedRfqs: 0,
    closedQuoteReceivedRfqs: 0,
    quotesReceivedRfqs: 0,
    poCreatedRfqs: 0,
    poPartiallyCreatedRfqs: 0,
    draftRfqs: 0,
  });
  const filterOptionsLoadedRef = useRef(false);
  const loadRequestIdRef = useRef(0);

  const anyMultiFilterActive =
    !!activeMultiFilters.status ||
    !!activeMultiFilters.recordStatus ||
    !!activeMultiFilters.clientReference ||
    !!activeMultiFilters.createdBy ||
    !!activeMultiFilters.fromDate ||
    !!activeMultiFilters.toDate;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        filterPanelRef.current &&
        !filterPanelRef.current.contains(e.target)
      ) {
        setIsFilterPanelOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function toApiDDMMYYYY(isoDate) {
    if (!isoDate) return "";
    const [yyyy, mm, dd] = isoDate.split("-");
    if (!yyyy || !mm || !dd) return "";
    return `${dd}-${mm}-${yyyy}`;
  }

  function extractExcelBase64(raw) {
    if (raw == null) return null;
    if (typeof raw === "string") {
      const t = raw.trim();
      if (t.startsWith("UEs")) return raw;
      if (t.startsWith("{")) {
        try {
          return extractExcelBase64(JSON.parse(t));
        } catch {
          return null;
        }
      }
      return null;
    }
    if (typeof raw === "object") {
      if (raw.body != null) return extractExcelBase64(raw.body);
      if (typeof raw.data === "string") return extractExcelBase64(raw.data);
      if (raw.data != null && typeof raw.data === "object") {
        const nested = extractExcelBase64(raw.data);
        if (nested) return nested;
      }
      if (raw.file != null) return extractExcelBase64(raw.file);
      if (raw.base64 != null) return extractExcelBase64(raw.base64);
    }
    return null;
  }

  function triggerXlsxDownloadFromBase64(base64, filenamePrefix = "RFQs") {
    if (!base64 || typeof base64 !== "string" || !base64.startsWith("UEs")) {
      throw new Error("Invalid Excel data received");
    }
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const timestamp = new Date()
      .toISOString()
      .replace(/[:.]/g, "-")
      .slice(0, -5);
    link.download = `${filenamePrefix}-${timestamp}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  const buildMultiFilterFields = useCallback(
    (fields = {}, { includeStatusFilters = true } = {}) => {
      if (includeStatusFilters && activeMultiFilters.status) {
        fields.rfqStatus = activeMultiFilters.status;
      }
      if (includeStatusFilters && activeMultiFilters.recordStatus) {
        fields.status = activeMultiFilters.recordStatus;
      }
      if (activeMultiFilters.clientReference) {
        fields.clientReferenceId = activeMultiFilters.clientReference;
      }
      if (activeMultiFilters.createdBy) {
        fields.createdBy = activeMultiFilters.createdBy;
      }
      if (activeMultiFilters.fromDate) {
        fields.fromDate = toApiDDMMYYYY(activeMultiFilters.fromDate);
      }
      if (activeMultiFilters.toDate) {
        fields.toDate = toApiDDMMYYYY(activeMultiFilters.toDate);
      }
      return fields;
    },
    [activeMultiFilters],
  );

  const fetchRfqs = useCallback(
    async (requestId) => {
      setIsLoading(true);
      try {
        const payload = {
          action: "get-all-rfqs",
          page: currentPage,
          limit: itemsPerPage,
        };

        const trimmedSearch = activeSearchQuery ? activeSearchQuery.trim() : "";
        if (trimmedSearch) {
          payload.search = trimmedSearch;
        }

        buildMultiFilterFields(payload);

        if (sortBy) {
          payload.sortBy = sortBy;
          payload.sortOrder = sortOrder;
        }

        const response = await axios.post(API_URL, payload);
        // Ignore stale responses when a newer filter/page request started.
        if (
          typeof requestId === "number" &&
          requestId !== loadRequestIdRef.current
        ) {
          return;
        }

        const responseData = normalizeApiPayload(
          parseApiResponse(response.data),
        );
        const { rfqs: list, pagination } = extractRfqsPayload(responseData);

        const ok =
          isApiSuccess(responseData) ||
          (Array.isArray(list) && list.length > 0);

        if (ok) {
          setRfqs(list);
          const total =
            typeof pagination.total === "number"
              ? pagination.total
              : typeof pagination.totalCount === "number"
                ? pagination.totalCount
                : list.length;
          setTotalCount(total);
        } else {
          toast.error(responseData?.message || "Failed to fetch RFQs", {
            autoClose: 2000,
          });
          setRfqs([]);
          setTotalCount(0);
        }
      } catch (error) {
        if (
          typeof requestId === "number" &&
          requestId !== loadRequestIdRef.current
        ) {
          return;
        }
        console.error("Fetch RFQs error:", error);
        const errData = error.response?.data
          ? parseApiResponse(error.response.data)
          : null;
        const msg =
          typeof errData === "object" && errData?.message
            ? errData.message
            : "An error occurred while fetching RFQs";
        toast.error(msg, { autoClose: 2000 });
        setRfqs([]);
        setTotalCount(0);
      } finally {
        if (
          typeof requestId !== "number" ||
          requestId === loadRequestIdRef.current
        ) {
          setIsLoading(false);
        }
      }
    },
    [
      currentPage,
      itemsPerPage,
      activeSearchQuery,
      sortBy,
      sortOrder,
      buildMultiFilterFields,
    ],
  );

  const fetchRfqCount = useCallback(
    async (requestId) => {
      setIsRfqCountLoading(true);

      try {
        const payload = { action: "rfq-count" };

        const trimmedSearch = activeSearchQuery ? activeSearchQuery.trim() : "";
        if (trimmedSearch) {
          payload.search = trimmedSearch;
        }

        buildMultiFilterFields(payload, { includeStatusFilters: false });

        const response = await axios.post(API_URL, payload);

        if (
          typeof requestId === "number" &&
          requestId !== loadRequestIdRef.current
        ) {
          return;
        }

        const responseData = normalizeApiPayload(
          parseApiResponse(response.data),
        );

        if (isApiSuccess(responseData)) {
          const status = responseData.status || {};

          setRfqCount({
            totalRfqs: status.totalRfqs || 0,
            openRfqs: status.openRfqs || 0,
            closedRfqs: status.closedRfqs || 0,
            closedQuoteReceivedRfqs: status.closedQuoteReceivedRfqs || 0,
            quotesReceivedRfqs: status.quotesReceivedRfqs || 0,
            poCreatedRfqs: status.poCreatedRfqs || 0,
            poPartiallyCreatedRfqs: status.poPartiallyCreatedRfqs || 0,
            draftRfqs: status.draftRfqs || 0,
          });
        }
      } catch (error) {
        if (
          typeof requestId === "number" &&
          requestId !== loadRequestIdRef.current
        ) {
          return;
        }
        console.error("RFQ Stats Error:", error);
      } finally {
        if (
          typeof requestId !== "number" ||
          requestId === loadRequestIdRef.current
        ) {
          setIsRfqCountLoading(false);
        }
      }
    },
    [activeSearchQuery, buildMultiFilterFields],
  );

  // Load order: 1) rfq-count → 2) get-all-rfqs → 3) get-rfq-users → 4) get-rfqs-client-reference
  // SessionWatcher runs validate-session on navigation / focus to sync role access.
  useEffect(() => {
    const requestId = ++loadRequestIdRef.current;
    let cancelled = false;

    const fetchRfqUsers = async () => {
      setIsRfqUsersLoading(true);
      try {
        const response = await axios.post(API_URL, { action: "get-rfq-users" });
        const responseData = normalizeApiPayload(
          parseApiResponse(response.data),
        );
        if (
          !cancelled &&
          isApiSuccess(responseData) &&
          Array.isArray(responseData.data)
        ) {
          setRfqUsers(normalizeRfqUsers(responseData.data));
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Fetch RFQ users error:", error);
        }
      } finally {
        if (!cancelled) {
          setIsRfqUsersLoading(false);
        }
      }
    };

    const fetchRfqClientReferences = async () => {
      setIsRfqClientReferencesLoading(true);
      try {
        const response = await axios.post(API_URL, {
          action: "get-rfqs-client-reference",
        });
        const responseData = normalizeApiPayload(
          parseApiResponse(response.data),
        );
        if (
          !cancelled &&
          isApiSuccess(responseData) &&
          Array.isArray(responseData.data)
        ) {
          setRfqClientReferences(
            normalizeRfqClientReferences(responseData.data),
          );
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Fetch RFQ client references error:", error);
        }
      } finally {
        if (!cancelled) {
          setIsRfqClientReferencesLoading(false);
        }
      }
    };

    const loadInOrder = async () => {
      await fetchRfqCount(requestId);
      if (cancelled || requestId !== loadRequestIdRef.current) return;

      await fetchRfqs(requestId);
      if (cancelled || requestId !== loadRequestIdRef.current) return;

      if (!filterOptionsLoadedRef.current) {
        await fetchRfqUsers();
        if (cancelled || requestId !== loadRequestIdRef.current) return;

        await fetchRfqClientReferences();
        if (!cancelled && requestId === loadRequestIdRef.current) {
          filterOptionsLoadedRef.current = true;
        }
      }
    };

    loadInOrder();

    return () => {
      cancelled = true;
    };
  }, [fetchRfqs, fetchRfqCount]);

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage) || 1);
  const startItem = rfqs.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endItem = Math.min(currentPage * itemsPerPage, totalCount);

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value, 10));
    setCurrentPage(1);
  };
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);

      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }, 100);
    }
  };

  const handleCreateRfqClick = () => {
    if (window.$) {
      window.$("#modalcreaterfq").modal("show");
    }
  };

  const handleConfirmCreateRfq = () => {
    if (window.$) {
      window.$("#modalcreaterfq").modal("hide");
    }
    navigate("/rfqCreate");
  };

  const handleDeleteClick = (rfq) => {
    const rid = rfq?._id || rfq?.id;
    if (!rid) {
      toast.error("RFQ ID is missing", { autoClose: 2000 });
      return;
    }
    setDeleteRfqId(rid);
    setDeleteRfqNumber(rfqNumber(rfq));
  };

  const handleDelete = async () => {
    if (!deleteRfqId) return;

    const requestUserId = localStorage.getItem("id");
    if (!requestUserId) {
      toast.error("User session expired. Please login again.", {
        autoClose: 2000,
      });
      return;
    }

    setIsDeleting(true);
    try {
      const response = await axios.post(API_URL, {
        action: "delete-rfq",
        id: deleteRfqId,
        requestUserId,
      });
      const responseData = normalizeApiPayload(parseApiResponse(response.data));

      if (isApiSuccess(responseData)) {
        toast.success(responseData?.message || "RFQ deleted successfully", {
          autoClose: 2000,
        });
        if (window.$) {
          window.$("#modaldelete").modal("hide");
        }
        setDeleteRfqId(null);
        setDeleteRfqNumber("");
        if (rfqs.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          const requestId = ++loadRequestIdRef.current;
          await fetchRfqCount(requestId);
          await fetchRfqs(requestId);
        }
      } else {
        toast.error(responseData?.message || "Failed to delete RFQ", {
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error("Delete RFQ error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      toast.error(errData?.message || "An error occurred while deleting RFQ", {
        autoClose: 2000,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7;
    const tp = totalPages;

    if (tp <= maxVisiblePages) {
      for (let i = 1; i <= tp; i++) pages.push(i);
    } else if (currentPage <= 4) {
      for (let i = 1; i <= 5; i++) pages.push(i);
      pages.push("ellipsis");
      pages.push(tp);
    } else if (currentPage >= tp - 3) {
      pages.push(1);
      pages.push("ellipsis");
      for (let i = tp - 4; i <= tp; i++) pages.push(i);
    } else {
      pages.push(1);
      pages.push("ellipsis");
      for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
      pages.push("ellipsis");
      pages.push(tp);
    }
    return pages;
  };

  useEffect(() => {
    sessionStorage.setItem(
      "rfqFilters",
      JSON.stringify({
        searchQuery,
        activeSearchQuery,
        currentPage,
        itemsPerPage,
        filterChecks,
        filterInputs,
        activeMultiFilters,
        sortBy,
        sortOrder,
      }),
    );
  }, [
    searchQuery,
    activeSearchQuery,
    currentPage,
    itemsPerPage,
    filterChecks,
    filterInputs,
    activeMultiFilters,
    sortBy,
    sortOrder,
  ]);

  const hasActiveFilters = Boolean(activeSearchQuery || anyMultiFilterActive);

  const handleFilterCheckToggle = (key) => {
    setFilterChecks((prev) => {
      const isCurrentlyChecked = prev[key];

      if (!isCurrentlyChecked) {
        const activeCount = Object.values(prev).filter(Boolean).length;
        if (activeCount >= MAX_ACTIVE_FILTERS) {
          toast.error(
            `You can select up to ${MAX_ACTIVE_FILTERS} filters only`,
            {
              autoClose: 2000,
            },
          );
          return prev;
        }
      }

      const next = { ...prev, [key]: !isCurrentlyChecked };

      if (!next[key]) {
        if (key === "dateRange") {
          setFilterInputs((inputs) => ({
            ...inputs,
            fromDate: "",
            toDate: "",
          }));
          setActiveMultiFilters((active) => ({
            ...active,
            fromDate: "",
            toDate: "",
          }));
        } else {
          setFilterInputs((inputs) => ({ ...inputs, [key]: "" }));
          setActiveMultiFilters((active) => ({ ...active, [key]: "" }));
        }
        if (currentPage !== 1) {
          setCurrentPage(1);
        }
      }

      return next;
    });
  };

  const handleFilterInputChange = (key, value) => {
    if (key === "fromDate" || key === "toDate") {
      const nextFrom = key === "fromDate" ? value : filterInputs.fromDate;
      const nextTo = key === "toDate" ? value : filterInputs.toDate;

      if (nextFrom && nextTo && new Date(nextFrom) > new Date(nextTo)) {
        toast.error("From date cannot be greater than to date", {
          autoClose: 2000,
        });
        return;
      }

      setFilterInputs((prev) => ({ ...prev, [key]: value }));
      setActiveMultiFilters((prev) => ({
        ...prev,
        fromDate: nextFrom,
        toDate: nextTo,
      }));
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
      return;
    }

    setFilterInputs((prev) => ({ ...prev, [key]: value }));
    setActiveMultiFilters((prev) => ({ ...prev, [key]: value }));
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  const handleClearFilter = () => {
    setFilterChecks(DEFAULT_FILTER_CHECKS);
    setFilterInputs(DEFAULT_FILTER_INPUTS);
    setActiveMultiFilters(DEFAULT_ACTIVE_MULTI_FILTERS);
    setIsFilterPanelOpen(false);
    if (currentPage !== 1) setCurrentPage(1);
  };

  const handleClearAll = () => {
    setSearchQuery("");
    setActiveSearchQuery("");
    handleClearFilter();
  };

  const handleSearchChange = (e) => {
    const newValue = e.target.value;
    setSearchQuery(newValue);
    const trimmedValue = newValue ? newValue.trim() : "";
    if (trimmedValue === "" && activeSearchQuery !== "") {
      setActiveSearchQuery("");
      if (currentPage !== 1) setCurrentPage(1);
    }
  };

  const handleSearchTrigger = () => {
    const trimmedQuery = searchQuery ? searchQuery.trim() : "";
    if (trimmedQuery === "") {
      setActiveSearchQuery("");
      if (currentPage !== 1) setCurrentPage(1);
    } else {
      setActiveSearchQuery(trimmedQuery);
      if (currentPage !== 1) setCurrentPage(1);
    }
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearchTrigger();
    }
  };

  const isRfqStatCardActive = (card) => {
    if (!card.filterValue) {
      return !activeMultiFilters.status && !activeMultiFilters.recordStatus;
    }
    return activeMultiFilters[card.filterType] === card.filterValue;
  };


  const handleRfqStatCardClick = (card) => {
    const cardFilterKey = card.filterType; // "status" | "recordStatus" | null

    if (!cardFilterKey) {
      setFilterChecks((prev) => ({
        ...prev,
        status: false,
        recordStatus: false,
      }));
      setFilterInputs((prev) => ({ ...prev, status: "", recordStatus: "" }));
      setActiveMultiFilters((prev) => ({
        ...prev,
        status: "",
        recordStatus: "",
      }));
      if (currentPage !== 1) setCurrentPage(1);
      return;
    }

    if (!filterChecks[cardFilterKey]) {
      const otherActiveKeys = Object.keys(filterChecks).filter(
        (key) => key !== cardFilterKey && filterChecks[key],
      );

      if (otherActiveKeys.length >= MAX_ACTIVE_FILTERS) {
        const keyToRemove = otherActiveKeys[0];
        const clearRemovedInputs =
          keyToRemove === "dateRange"
            ? { fromDate: "", toDate: "" }
            : { [keyToRemove]: "" };
        setFilterChecks((prev) => ({
          ...prev,
          [keyToRemove]: false,
          [cardFilterKey]: true,
        }));
        setFilterInputs((prev) => ({
          ...prev,
          ...clearRemovedInputs,
          [cardFilterKey]: card.filterValue,
        }));
        setActiveMultiFilters((prev) => ({
          ...prev,
          ...clearRemovedInputs,
          [cardFilterKey]: card.filterValue,
        }));
        toast.info("Replaced an existing filter to apply this status", {
          autoClose: 2000,
        });
      } else {
        setFilterChecks((prev) => ({ ...prev, [cardFilterKey]: true }));
        setFilterInputs((prev) => ({
          ...prev,
          [cardFilterKey]: card.filterValue,
        }));
        setActiveMultiFilters((prev) => ({
          ...prev,
          [cardFilterKey]: card.filterValue,
        }));
      }
    } else {
      setFilterInputs((prev) => ({
        ...prev,
        [cardFilterKey]: card.filterValue,
      }));
      setActiveMultiFilters((prev) => ({
        ...prev,
        [cardFilterKey]: card.filterValue,
      }));
    }

    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  const handleExportRfqs = async () => {
    if (exportMode === "date") {
      if (!exportFromDate || !exportToDate) {
        toast.error("Please select both from date and to date", {
          autoClose: 2000,
        });
        return;
      }
      if (new Date(exportFromDate) > new Date(exportToDate)) {
        toast.error("From date cannot be greater than to date", {
          autoClose: 2000,
        });
        return;
      }
    }

    setIsExporting(true);

    try {
      const payload = {
        action: "rfq-export",
      };

      if (exportMode === "date") {
        payload.fromDate = toApiDDMMYYYY(exportFromDate);
        payload.toDate = toApiDDMMYYYY(exportToDate);
      }

      const trimmedSearch = activeSearchQuery ? activeSearchQuery.trim() : "";
      if (trimmedSearch) {
        payload.search = trimmedSearch;
      }

      buildMultiFilterFields(payload);

      const response = await axios.post(API_URL, payload, {
        responseType: "json",
      });

      const base64 = extractExcelBase64(response.data);
      if (!base64) {
        throw new Error("Invalid Excel data received from server");
      }

      triggerXlsxDownloadFromBase64(base64, "RFQs");

      toast.success("RFQs exported successfully.", {
        autoClose: 2000,
      });
      setShowExportModal(false);
    } catch (error) {
      console.error("Export RFQs error:", error);
      toast.error("Failed to export RFQs.", {
        autoClose: 2000,
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleSort = (column) => {
    const apiField = RFQ_SORT_COLUMN_MAP[column] || column;

    if (sortBy === apiField) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
      } else {
        setSortBy(DEFAULT_SORT_BY);
        setSortOrder(DEFAULT_SORT_ORDER);
      }
    } else {
      setSortBy(apiField);
      setSortOrder("asc");
    }

    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  const getSortIcon = (column) => {
    const apiField = RFQ_SORT_COLUMN_MAP[column] || column;

    if (sortBy !== apiField) {
      return (
        <i
          className="fa-solid fa-sort text-muted ml-1"
          style={{ fontSize: "0.8em", flexShrink: 0 }}
        />
      );
    }

    if (sortOrder === "asc") {
      return (
        <i
          className="fa-solid fa-sort-up text-primary ml-1"
          style={{ fontSize: "0.8em", flexShrink: 0 }}
        />
      );
    }

    return (
      <i
        className="fa-solid fa-sort-down text-primary ml-1"
        style={{ fontSize: "0.8em", flexShrink: 0 }}
      />
    );
  };

  const renderSortableHeader = (column, label, extraStyle = {}) => (
    <th
      onClick={() => handleSort(column)}
      style={{
        cursor: "pointer",
        whiteSpace: "nowrap",
        ...extraStyle,
      }}
    >
      <span style={{ display: "inline-flex", alignItems: "center" }}>
        {label}
        {getSortIcon(column)}
      </span>
    </th>
  );

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2 align-items-center">
            <div className="col-sm-6">
              <h1 className="m-0 txt_lft">RFQ</h1>
            </div>

            <div className="col-sm-6">
              <div className="d-flex justify-content-end align-items-center">
                {canCreateRfq && (
                  <button
                    type="button"
                    className="btn btn-blue mr-2"
                    onClick={handleCreateRfqClick}
                  >
                    Create RFQ
                  </button>
                )}

                <button
                  type="button"
                  className="btn btn-success"
                  onClick={() => setShowExportModal(true)}
                  disabled={isExporting}
                >
                  <i
                    className={`fa-solid ${
                      isExporting ? "fa-spinner fa-spin" : "fa-file-export"
                    } mr-1`}
                  />
                  {isExporting ? "Exporting..." : "Export"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="procurement-stat-card-row rfq-stat-card-row">
                {isRfqCountLoading
                  ? RFQ_STAT_CARDS.map((card) => (
                      <RfqStatCardSkeleton key={card.key} />
                    ))
                  : RFQ_STAT_CARDS.map((card) => (
                      <RfqStatCard
                        key={card.key}
                        label={card.label}
                        value={rfqCount[card.key]}
                        titleColor={card.titleColor}
                        valueColor={card.valueColor}
                        isActive={isRfqStatCardActive(card)}
                        onClick={() => handleRfqStatCardClick(card)}
                      />
                    ))}
              </div>
              <div>
                <div className="card-body">
                  <div className="row align-items-end mb-4 flex-xl-nowrap">
                    <div className="col-12 col-sm-6 col-md-4 col-xl-3 mb-2 mb-xl-0">
                      <div className="form-group mb-0">
                        <label
                          className="mb-1"
                          style={{
                            fontSize: "13px",
                            fontWeight: "500",
                          }}
                        >
                          Search
                        </label>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search RFQs..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            onKeyPress={handleSearchKeyPress}
                          />
                          <div className="input-group-append">
                            <div
                              className="input-group-text crsr_pntr"
                              onClick={handleSearchTrigger}
                              style={{ cursor: "pointer" }}
                            >
                              <i className="fa-solid fa-search" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                      <div
                        className="form-group mb-0 position-relative"
                        ref={filterPanelRef}
                      >
                        <label
                          className="mb-1"
                          style={{
                            fontSize: "13px",
                            fontWeight: "500",
                          }}
                        >
                          Filter
                        </label>

                        <button
                          type="button"
                          className="form-control text-left d-flex justify-content-between align-items-center"
                          onClick={() => setIsFilterPanelOpen((open) => !open)}
                        >
                          <span
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {anyMultiFilterActive
                              ? "Filters"
                              : "Select Filters"}
                          </span>

                          <i
                            className={`fa-solid fa-chevron-${
                              isFilterPanelOpen ? "up" : "down"
                            }`}
                            style={{
                              fontSize: "11px",
                              flexShrink: 0,
                              marginLeft: "6px",
                            }}
                          />
                        </button>

                        {isFilterPanelOpen && (
                          <div
                            className="rfq-filter-dropdown"
                            style={{
                              position: "absolute",
                              zIndex: 20,
                              top: "100%",
                              left: 0,
                              marginTop: "4px",
                              background: "#fff",
                              border: "1px solid #ced4da",
                              borderRadius: "4px",
                              padding: "10px 12px",
                              width: "200px",
                              maxWidth: "calc(100vw - 32px)",
                              boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
                            }}
                          >
                            {[
                              {
                                key: "status",
                                id: "filterCheckStatus",
                                label: "Status",
                              },
                              {
                                key: "recordStatus",
                                id: "filterCheckRecordStatus",
                                label: "Record Status",
                              },
                              {
                                key: "clientReference",
                                id: "filterCheckClient",
                                label: "Client",
                              },
                              {
                                key: "createdBy",
                                id: "filterCheckCreatedBy",
                                label: "Created User",
                              },
                              {
                                key: "dateRange",
                                id: "filterCheckDateRange",
                                label: "Date",
                              },
                            ].map((filter) => {
                              const activeCount =
                                Object.values(filterChecks).filter(
                                  Boolean,
                                ).length;

                              return (
                                <div
                                  className="custom-control custom-checkbox mb-2"
                                  key={filter.key}
                                >
                                  <input
                                    type="checkbox"
                                    className="custom-control-input"
                                    id={filter.id}
                                    checked={filterChecks[filter.key]}
                                    disabled={
                                      !filterChecks[filter.key] &&
                                      activeCount >= MAX_ACTIVE_FILTERS
                                    }
                                    onChange={() =>
                                      handleFilterCheckToggle(filter.key)
                                    }
                                  />

                                  <label
                                    className="custom-control-label"
                                    htmlFor={filter.id}
                                  >
                                    {filter.label}
                                  </label>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>

                    {filterChecks.status && (
                      <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                        <div className="form-group mb-0">
                          <label
                            className="mb-1"
                            style={{
                              fontSize: "13px",
                              fontWeight: "500",
                            }}
                          >
                            Status
                          </label>

                          <select
                            className="form-control"
                            value={filterInputs.status}
                            onChange={(e) =>
                              handleFilterInputChange("status", e.target.value)
                            }
                          >
                            <option value="">Select Status</option>
                            {RFQ_STATUS_OPTIONS.map((status) => (
                              <option key={status.value} value={status.value}>
                                {status.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}

                    {filterChecks.recordStatus && (
                      <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                        <div className="form-group mb-0">
                          <label
                            className="mb-1"
                            style={{
                              fontSize: "13px",
                              fontWeight: "500",
                            }}
                          >
                            Record Status
                          </label>

                          <select
                            className="form-control"
                            value={filterInputs.recordStatus}
                            onChange={(e) =>
                              handleFilterInputChange(
                                "recordStatus",
                                e.target.value,
                              )
                            }
                          >
                            <option value="">Select Record Status</option>
                            {RFQ_RECORD_STATUS_OPTIONS.map((status) => (
                              <option key={status} value={status}>
                                {status}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}

                    {filterChecks.clientReference && (
                      <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                        <div className="form-group mb-0">
                          <label
                            className="mb-1"
                            style={{
                              fontSize: "13px",
                              fontWeight: "500",
                            }}
                          >
                            Client
                          </label>

                          <select
                            className="form-control"
                            value={filterInputs.clientReference}
                            onChange={(e) =>
                              handleFilterInputChange(
                                "clientReference",
                                e.target.value,
                              )
                            }
                          >
                            <option value="">
                              {isRfqClientReferencesLoading &&
                              rfqClientReferences.length === 0
                                ? "Loading clients..."
                                : "Select Client"}
                            </option>
                            {rfqClientReferences.map((client) => (
                              <option
                                key={client.clientReferenceId}
                                value={client.clientReferenceId}
                              >
                                {client.clientReference}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}

                    {filterChecks.createdBy && (
                      <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                        <div className="form-group mb-0">
                          <label
                            className="mb-1"
                            style={{
                              fontSize: "13px",
                              fontWeight: "500",
                            }}
                          >
                            Created User
                          </label>

                          <select
                            className="form-control"
                            value={filterInputs.createdBy}
                            onChange={(e) =>
                              handleFilterInputChange(
                                "createdBy",
                                e.target.value,
                              )
                            }
                          >
                            <option value="">
                              {isRfqUsersLoading && rfqUsers.length === 0
                                ? "Loading users..."
                                : "Select User"}
                            </option>
                            {rfqUsers.map((user) => (
                              <option key={user._id} value={user._id}>
                                {user.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}

                    {filterChecks.dateRange && (
                      <>
                        <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                          <div className="form-group mb-0">
                            <label
                              className="mb-1"
                              style={{ fontSize: "13px", fontWeight: "500" }}
                            >
                              From Date
                            </label>
                            <input
                              type="date"
                              className="form-control"
                              value={filterInputs.fromDate || ""}
                              max={filterInputs.toDate || undefined}
                              onChange={(e) =>
                                handleFilterInputChange(
                                  "fromDate",
                                  e.target.value,
                                )
                              }
                            />
                          </div>
                        </div>
                        <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                          <div className="form-group mb-0">
                            <label
                              className="mb-1"
                              style={{ fontSize: "13px", fontWeight: "500" }}
                            >
                              To Date
                            </label>
                            <input
                              type="date"
                              className="form-control"
                              value={filterInputs.toDate || ""}
                              min={filterInputs.fromDate || undefined}
                              onChange={(e) =>
                                handleFilterInputChange(
                                  "toDate",
                                  e.target.value,
                                )
                              }
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {anyMultiFilterActive && (
                      <div className="col-12 col-sm-6 col-md-4 col-xl-auto mb-2 mb-xl-0">
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm w-100"
                          onClick={handleClearFilter}
                          style={{
                            minWidth: "100px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <i className="fa-solid fa-times mr-1" />
                          Clear Filter
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {isLoading && rfqs.length === 0 ? (
                <div className="text-center p-4">
                  <PulseLoader color="#1199B2" size={10} />
                </div>
              ) : rfqs.length === 0 && !hasActiveFilters ? (
                <div className="nocontent_div text-center p-4">
                  There are no RFQs yet.
                  <br />
                  {canCreateRfq && (
                    <div className="mt-3">
                      <button
                        type="button"
                        className="btn btn-blue"
                        onClick={handleCreateRfqClick}
                      >
                        Create RFQ
                      </button>
                    </div>
                  )}
                </div>
              ) : rfqs.length === 0 && hasActiveFilters ? (
                <div className="nocontent_div text-center p-4">
                  No RFQs found matching your filters.
                  <br />
                  <div className="mt-3">
                    <button
                      type="button"
                      className="btn btn-blue"
                      onClick={handleClearAll}
                    >
                      Clear Filters
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  className="supply-table-container"
                  style={{ position: "relative" }}
                >
                  {isLoading && (
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "rgba(255, 255, 255, 0.55)",
                        pointerEvents: "none",
                      }}
                    >
                      <PulseLoader color="#1199B2" size={10} />
                    </div>
                  )}
                  <div
                    className="table-responsive"
                    style={{
                      opacity: isLoading ? 0.6 : 1,
                      transition: "opacity 0.15s ease",
                      pointerEvents: isLoading ? "none" : "auto",
                    }}
                  >
                    <table
                      id="rfqs"
                      width="100%"
                      className="table table-striped table-bordered txt_lft"
                    >
                      <thead>
                        <tr>
                          <th
                            style={{
                              whiteSpace: "nowrap",
                              width: "40px",
                              minWidth: "40px",
                            }}
                          >
                            S.No
                          </th>
                          {renderSortableHeader("rfqNumber", "RFQ No", {
                            minWidth: "120px",
                          })}
                          {renderSortableHeader("rfqDate", "RFQ Date", {
                            minWidth: "100px",
                          })}
                          {renderSortableHeader("rfqDueDate", "RFQ Due Date", {
                            minWidth: "100px",
                          })}
                          <th style={{ minWidth: "100px" }}>PR Number</th>
                          <th style={{ minWidth: "110px" }}>Client</th>
                          <th style={{ minWidth: "180px" }}>Summary</th>
                          {renderSortableHeader("status", "Status", {
                            minWidth: "90px",
                          })}
                          {renderSortableHeader("rfqStatus", "RFQ Status", {
                            minWidth: "100px",
                          })}
                          {renderSortableHeader("createdBy", "Created User", {
                            minWidth: "140px",
                          })}
                          <th style={{ width: "80px", minWidth: "80px" }}>
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {rfqs.map((rfq, index) => {
                          const rid = rfq._id || rfq.id;
                          return (
                            <tr key={rid || index}>
                              <td>
                                {(currentPage - 1) * itemsPerPage + index + 1}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {rfqNumber(rfq)}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {rfqDateValue(rfq)}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {rfqDueDateValue(rfq)}
                              </td>
                              <td
                                style={{
                                  maxWidth: "140px",
                                  wordWrap: "break-word",
                                  whiteSpace: "normal",
                                }}
                              >
                                {renderRfqPrNumbers(rfq)}
                              </td>
                              <td
                                style={{
                                  maxWidth: "140px",
                                  wordWrap: "break-word",
                                  whiteSpace: "normal",
                                }}
                              >
                                {renderRfqClientReferences(rfq)}
                              </td>
                              <td
                                style={{
                                  maxWidth: "240px",
                                  wordWrap: "break-word",
                                  whiteSpace: "normal",
                                }}
                              >
                                {rfqSummaryValue(rfq)}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {renderRfqRecordStatus(rfq.status)}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {formatRfqStatusWithQuoteCount(rfq)}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {rfqCreatedByName(rfq)}
                              </td>
                              <td>
                                <div
                                  align="center"
                                  style={{ whiteSpace: "nowrap" }}
                                  className="d-flex justify-content-center align-items-center"
                                >
                                  {canViewRfq && (
                                    <Link
                                      to={`/rfqView/${rid}`}
                                      className="crsr_pntr mr-2"
                                      title="View"
                                    >
                                      <i className="fa-solid fa-eye" />
                                    </Link>
                                  )}
                                  {canUpdateRfq && (
                                    <Link
                                      to={`/rfqCreate/${rid}`}
                                      className="crsr_pntr mr-2"
                                      title="Edit"
                                    >
                                      <i className="fa-solid fa-pen" />
                                    </Link>
                                  )}
                                  {canDeleteRfq && (
                                    <a
                                      href="#modaldelete"
                                      className="crsr_pntr action-icon-link"
                                      data-toggle="modal"
                                      data-target="#modaldelete"
                                      title="Delete"
                                      onClick={() => handleDeleteClick(rfq)}
                                    >
                                      <i className="fa-solid fa-trash-can" />
                                    </a>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {rfqs.length > 0 && (
                    <div
                      className="supply-pagination-container"
                      style={{
                        opacity: isLoading ? 0.65 : 1,
                        pointerEvents: isLoading ? "none" : "auto",
                      }}
                    >
                      <div className="pagination-wrapper">
                        {/* Entries Section */}
                        <div className="pagination-info">
                          <label
                            className="mb-0 mr-2"
                            style={{ fontSize: "13px" }}
                          >
                            Show:
                          </label>

                          <select
                            className="form-control form-control-sm"
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                          >
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                          </select>

                          <span className="ml-2" style={{ fontSize: "13px" }}>
                            entries
                          </span>

                          <span
                            className="ml-3 text-muted"
                            style={{ fontSize: "13px" }}
                          >
                            {startItem}–{endItem} of {totalCount}
                          </span>
                        </div>

                        {/* Pagination Buttons */}
                        <nav className="pagination-nav">
                          <ul className="pagination mb-0">
                            <li
                              className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(1)}
                                disabled={currentPage === 1}
                                title="First Page"
                              >
                                <i className="fa-solid fa-angle-double-left"></i>
                              </button>
                            </li>

                            <li
                              className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                            >
                              <button
                                className="page-link"
                                onClick={() =>
                                  handlePageChange(currentPage - 1)
                                }
                                disabled={currentPage === 1}
                                title="Previous Page"
                              >
                                <i className="fa-solid fa-angle-left"></i>
                              </button>
                            </li>

                            {getPageNumbers().map((page, index) =>
                              page === "ellipsis" ? (
                                <li
                                  key={`ellipsis-${index}`}
                                  className="page-item disabled"
                                >
                                  <span className="page-link">...</span>
                                </li>
                              ) : (
                                <li
                                  key={page}
                                  className={`page-item ${
                                    currentPage === page ? "active" : ""
                                  }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => handlePageChange(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              ),
                            )}

                            <li
                              className={`page-item ${
                                currentPage === totalPages ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() =>
                                  handlePageChange(currentPage + 1)
                                }
                                disabled={currentPage === totalPages}
                                title="Next Page"
                              >
                                <i className="fa-solid fa-angle-right"></i>
                              </button>
                            </li>

                            <li
                              className={`page-item ${
                                currentPage === totalPages ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(totalPages)}
                                disabled={currentPage === totalPages}
                                title="Last Page"
                              >
                                <i className="fa-solid fa-angle-double-right"></i>
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade"
        id="modalcreaterfq"
        tabIndex={-1}
        aria-labelledby="modalcreaterfqLabel"
        aria-hidden="true"
        data-backdrop="static"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="modalcreaterfqLabel">
                Create RFQ
              </h5>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body">
              <div className="w-100 d-inline-block text-center">
                Are you sure you want to create RFQ?
              </div>
            </div>
            <div className="modal-footer justify-content-center">
              <button
                type="button"
                className="btn btn-secondary mr-2"
                data-dismiss="modal"
              >
                No
              </button>
              <button
                type="button"
                className="btn btn-blue"
                onClick={handleConfirmCreateRfq}
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade"
        id="modaldelete"
        tabIndex={-1}
        aria-labelledby="modaldeleteLabel"
        aria-hidden="true"
        data-backdrop="static"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="modaldeleteLabel">
                Delete RFQ
              </h5>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
                disabled={isDeleting}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body">
              <div className="w-100 d-inline-block text-center">
                Are you sure you want to delete
                <br />
                <span className="fwt_600">{deleteRfqNumber || "-"}</span>?
              </div>
            </div>
            <div className="modal-footer justify-content-center">
              <button
                type="button"
                className="btn btn-secondary mr-2"
                data-dismiss="modal"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Yes, delete"}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div
        className={`modal fade ${showExportModal ? "show" : ""}`}
        style={{ display: showExportModal ? "block" : "none" }}
        tabIndex={-1}
        aria-labelledby="modalExportRfqLabel"
        aria-hidden={!showExportModal}
        onClick={() => {
          if (!isExporting) setShowExportModal(false);
        }}
      >
        <div
          className="modal-dialog modal-dialog-centered"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="modalExportRfqLabel">
                Export RFQs
              </h5>
              <button
                type="button"
                className="close"
                aria-label="Close"
                onClick={() => {
                  if (!isExporting) setShowExportModal(false);
                }}
                disabled={isExporting}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body">
              {hasActiveFilters && (
                <p className="text-muted small mb-3">
                  Current filters will be included:
                  {activeSearchQuery && (
                    <span className="d-block">Search: {activeSearchQuery}</span>
                  )}
                  {activeMultiFilters.status && (
                    <span className="d-block">
                      RFQ Status: {activeMultiFilters.status}
                    </span>
                  )}
                  {activeMultiFilters.recordStatus && (
                    <span className="d-block">
                      Record Status: {activeMultiFilters.recordStatus}
                    </span>
                  )}
                  {activeMultiFilters.createdBy && (
                    <span className="d-block">Created User selected</span>
                  )}
                  {activeMultiFilters.clientReference && (
                    <span className="d-block">Client filter selected</span>
                  )}
                  {(activeMultiFilters.fromDate ||
                    activeMultiFilters.toDate) && (
                    <span className="d-block">
                      Date:{" "}
                      {activeMultiFilters.fromDate
                        ? toApiDDMMYYYY(activeMultiFilters.fromDate)
                        : "-"}{" "}
                      to{" "}
                      {activeMultiFilters.toDate
                        ? toApiDDMMYYYY(activeMultiFilters.toDate)
                        : "-"}
                    </span>
                  )}
                </p>
              )}
              <div className="form-group mb-3">
                <label className="mb-2 d-block">Export Type</label>
                <div className="custom-control custom-radio custom-control-inline">
                  <input
                    type="radio"
                    id="exportAllRfqs"
                    name="exportTypeRfqs"
                    className="custom-control-input"
                    value="all"
                    checked={exportMode === "all"}
                    onChange={(e) => setExportMode(e.target.value)}
                    disabled={isExporting}
                  />
                  <label
                    className="custom-control-label"
                    htmlFor="exportAllRfqs"
                  >
                    All
                  </label>
                </div>
                <div className="custom-control custom-radio custom-control-inline">
                  <input
                    type="radio"
                    id="exportDateRfqs"
                    name="exportTypeRfqs"
                    className="custom-control-input"
                    value="date"
                    checked={exportMode === "date"}
                    onChange={(e) => setExportMode(e.target.value)}
                    disabled={isExporting}
                  />
                  <label
                    className="custom-control-label"
                    htmlFor="exportDateRfqs"
                  >
                    Filter by date
                  </label>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group col-md-6">
                  <label className="mb-1">From Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={exportFromDate}
                    onChange={(e) => setExportFromDate(e.target.value)}
                    disabled={isExporting || exportMode !== "date"}
                    max={exportToDate || undefined}
                  />
                </div>
                <div className="form-group col-md-6 mb-0">
                  <label className="mb-1">To Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={exportToDate}
                    onChange={(e) => setExportToDate(e.target.value)}
                    disabled={isExporting || exportMode !== "date"}
                    min={exportFromDate || undefined}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowExportModal(false)}
                disabled={isExporting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-blue"
                onClick={handleExportRfqs}
                disabled={isExporting}
              >
                {isExporting ? "Exporting..." : "Export"}
              </button>
            </div>
          </div>
        </div>
      </div>
      {showExportModal && <div className="modal-backdrop fade show" />}
    </div>
  );
};

export default Rfq;
